import { type ComponentPropsWithRef, createElement, useId } from 'react'
import { createPortal } from 'react-dom'

type Tag = 'a' | 'button' | 'div' | 'li' | 'output' | 'span' | 'td' | 'time'

/** Keep the original native element, handlers and layout. Descriptions live
 *  outside the control's container so sibling selectors and grid tracks keep
 *  their original children. IDREFs preserve the accessible description. */
export function Described<T extends Tag>({
  as,
  description,
  'aria-describedby': describedBy,
  ...props
}: {
  as: T
  description?: string
} & Omit<ComponentPropsWithRef<T>, 'title'>) {
  const id = useId()
  const hasDescription = Boolean(description?.trim()) && description !== props['aria-label']
  const ids = [describedBy, hasDescription ? id : undefined].filter(Boolean).join(' ')
  return (
    <>
      {createElement(as, { ...props, 'aria-describedby': ids || undefined })}
      {hasDescription && typeof document !== 'undefined'
        ? createPortal(
            <span id={id} hidden style={{ display: 'none' }}>
              {description}
            </span>,
            document.body
          )
        : null}
    </>
  )
}
